'use client';

import React, { useState } from 'react';
import {
    Bell,
    ChevronDown,
    LogOut,
    User,
    Baby,
    Check,
    Settings
} from 'lucide-react';
import Link from 'next/link';

interface Student {
    id: string;
    name: string;
    school: string;
    grade: string;
}

const dummyStudents: Student[] = [
    { id: '1', name: 'Ahmad Al-Fatih', school: 'SD Pekanbaru', grade: 'Kelas 4A' },
    { id: '2', name: 'Siti Aminah', school: 'SMP Pekanbaru', grade: 'Kelas 8B' },
];

interface HeaderProps {
    onMenuClick?: () => void;
}

export default function WalimuridHeader({ onMenuClick }: HeaderProps) {
    const [selectedStudent, setSelectedStudent] = useState<Student>(dummyStudents[0]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    return (
        <header className="h-[96px] min-h-[96px] flex-shrink-0 bg-surface/40 backdrop-blur-xl px-10 border-b border-border-main flex items-center justify-between sticky top-0 z-40">
            {/* Mobile Menu Button */}
            <button
                onClick={onMenuClick}
                className="lg:hidden p-3 bg-surface border border-border-main rounded-2xl text-muted hover:text-primary transition-all mr-4"
            >
                <ChevronDown className="w-5 h-5 -rotate-90" />
            </button>

            {/* Left: Student Switcher */}
            <div className="flex items-center gap-6 flex-1 lg:flex-none">
                <div className="relative">
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex items-center gap-4 bg-background border-2 border-border-main pl-2 pr-5 py-2 rounded-2xl hover:border-primary/50 transition-all shadow-sm group"
                    >
                        <div className="w-10 h-10 rounded-xl bg-primary-soft flex items-center justify-center text-primary group-hover:scale-105 transition-transform">
                            <Baby className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-none mb-1">Pilih Data Anak</p>
                            <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                                {selectedStudent.name}
                                <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </h3>
                        </div>
                    </button>

                    {/* Student Dropdown */}
                    {isDropdownOpen && (
                        <div className="absolute top-full left-0 mt-3 w-72 bg-surface border border-border-main rounded-3xl shadow-2xl shadow-primary/10 p-3 animate-in fade-in slide-in-from-top-2 duration-200">
                            <p className="px-4 py-3 text-[10px] font-bold text-muted-foreground uppercase tracking-widest border-b border-border-main/50 mb-2">Daftar Anak Anda</p>
                            <div className="space-y-1">
                                {dummyStudents.map((child) => (
                                    <button
                                        key={child.id}
                                        onClick={() => {
                                            setSelectedStudent(child);
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all ${selectedStudent.id === child.id
                                            ? 'bg-primary-soft/50 border border-primary/20'
                                            : 'hover:bg-primary-soft/30'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold ${selectedStudent.id === child.id ? 'bg-primary text-white' : 'bg-surface border border-border-main text-muted'
                                                }`}>
                                                {child.name.charAt(0)}
                                            </div>
                                            <div className="text-left">
                                                <p className="text-xs font-bold text-foreground">{child.name}</p>
                                                <p className="text-[10px] font-medium text-muted-foreground">{child.school} • {child.grade}</p>
                                            </div>
                                        </div>
                                        {selectedStudent.id === child.id && <Check className="w-4 h-4 text-primary" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Right: Notification & Parent Profile */}
            <div className="flex items-center gap-8">
                {/* Notification */}
                <button className="relative w-12 h-12 rounded-2xl bg-background border-2 border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary/50 transition-all group">
                    <Bell className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                    <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-error rounded-full ring-4 ring-background"></span>
                </button>

                {/* Parent Info */}
                <div className="flex items-center gap-4 pl-8 border-l border-border-main/50 relative">
                    <button
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-4 focus:outline-none group/profile p-1.5 pr-4 rounded-3xl hover:bg-surface border border-transparent hover:border-border-main transition-all"
                    >
                        <div className="text-right hidden md:block pr-2">
                            <p className="text-sm font-black text-foreground tracking-tight group-hover/profile:text-primary transition-colors">Bpk. Sudirman Hartono</p>
                            <p className="text-[10px] font-bold text-primary uppercase tracking-widest opacity-80">Walimurid (Parent)</p>
                        </div>
                        <div className="relative">
                            <div className="w-12 h-12 rounded-[1.25rem] bg-primary flex items-center justify-center text-white font-black text-xl shadow-lg shadow-primary/20 transition-transform group-hover/profile:scale-105 border-2 border-surface">
                                SH
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-success border-4 border-surface rounded-full shadow-sm"></div>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-muted transition-transform group-hover/profile:text-primary ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Profile Dropdown */}
                    {isProfileOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-0"
                                onClick={() => setIsProfileOpen(false)}
                            ></div>
                            <div className="absolute top-full mt-4 right-0 w-72 bg-surface border border-border-main rounded-[2.5rem] shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-200 p-5 overflow-hidden font-sans">
                                <div className="px-3 py-4 mb-4">
                                    <p className="text-sm font-bold text-foreground leading-tight">Bpk. Sudirman Hartono</p>
                                    <p className="text-[11px] text-muted font-medium mt-1 truncate">sudirman.h@gmail.com</p>
                                </div>
                                <div className="h-px bg-border-main/50 mb-4 mx-2"></div>
                                <div className="space-y-1">
                                    <Link
                                        href="/walimurid/profile"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <User className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Profil Saya
                                    </Link>
                                    <Link
                                        href="/walimurid/settings"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-secondary hover:bg-primary-soft hover:text-primary rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <Settings className="w-5 h-5 opacity-50 group-hover/item:opacity-100 transition-opacity" /> Pengaturan
                                    </Link>
                                    <div className="h-px bg-border-main/50 my-4 mx-2"></div>
                                    <Link
                                        href="/login"
                                        className="w-full text-left px-4 py-3.5 text-xs font-bold text-error hover:bg-error-soft rounded-2xl transition-all flex items-center gap-3.5 group/item"
                                    >
                                        <LogOut className="w-5 h-5 opacity-70 group-hover/item:opacity-100 transition-opacity" /> Keluar
                                    </Link>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
